import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SendIcon } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/textarea';
import { TaskMessageBubble } from './TaskMessageBubble';
import { TaskAttachmentUploadArea } from './TaskAttachmentUploadArea';
import { AttachmentRow } from './AttachmentRow';
import { useStagedAttachments } from '@/hooks/useStagedAttachments';
import { useTaskMessageEditing } from '@/hooks/useTaskMessageEditing';
import type {
  CompanyTaskMessage,
  CompanyTaskAttachmentInputDto,
} from '@gokrew/models/tasks';

type CommunicationAccent = 'indigo' | 'violet';

const SEND_BUTTON_ACCENT: Record<CommunicationAccent, string> = {
  indigo: 'bg-indigo-600 hover:bg-indigo-700',
  violet: 'bg-violet-600 hover:bg-violet-700',
};

const SELECTOR_ACTIVE_ACCENT: Record<CommunicationAccent, string> = {
  indigo: 'border-indigo-500 bg-indigo-600 text-white',
  violet: 'border-violet-500 bg-violet-50 text-violet-700',
};

const TEXTAREA_FOCUS_ACCENT: Record<CommunicationAccent, string> = {
  indigo: 'focus:border-indigo-400 focus:ring-indigo-400',
  violet: 'focus:border-violet-400 focus:ring-violet-400',
};

export interface TaskCommunicationThreadOption {
  id: string;
  label: string;
}

interface TaskCommunicationPanelProps {
  threadId: string | undefined;
  messages: CompanyTaskMessage[];
  currentUserId: string | undefined;
  language: string;
  isSending: boolean;
  onSend: (payload: {
    body: string;
    attachments?: CompanyTaskAttachmentInputDto[];
  }) => Promise<void> | void;
  updateMessage: (
    args: { threadId: string; messageId: string; body: string },
    options: { onSuccess: () => void }
  ) => void;
  deleteMessage: (args: { threadId: string; messageId: string }) => void;
  uploadFile: (file: File) => Promise<{ url: string }>;
  accent?: CommunicationAccent;
  /** Only rendered as a selector row when more than one option is given. */
  threads?: TaskCommunicationThreadOption[];
  onThreadChange?: (threadId: string) => void;
  listClassName?: string;
  className?: string;
}

export function TaskCommunicationPanel({
  threadId,
  messages,
  currentUserId,
  language,
  isSending,
  onSend,
  updateMessage,
  deleteMessage,
  uploadFile,
  accent = 'indigo',
  threads,
  onThreadChange,
  listClassName,
  className,
}: TaskCommunicationPanelProps) {
  const [messageText, setMessageText] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const attachments = useStagedAttachments(uploadFile);

  const { t } = useTranslation(TranslationNS.LibsCommon);

  const messageBubbleProps = useTaskMessageEditing({
    threadId,
    updateMessage,
    deleteMessage,
    editedLabel: t('tasks.communication.edited'),
    saveLabel: t('tasks.communication.save'),
    cancelLabel: t('tasks.communication.cancel'),
  });

  // Scroll the message list itself — never the sentinel-based
  // `scrollIntoView`, which bubbles up through every scrollable ancestor
  // (e.g. a dialog's own scroll container) and can scroll the whole dialog
  // instead of just this list.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const body = messageText.trim();
    if (!body || !threadId || isSending) return;
    await onSend({
      body,
      ...(attachments.readyAttachments.length
        ? { attachments: attachments.readyAttachments }
        : {}),
    });
    setMessageText('');
    attachments.reset();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`flex flex-1 flex-col min-h-0 ${className ?? ''}`}>
      {threads && threads.length > 1 && (
        <div className="flex gap-1.5 pb-3 flex-shrink-0">
          {threads.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onThreadChange?.(option.id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                threadId === option.id
                  ? SELECTOR_ACTIVE_ACCENT[accent]
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {!threadId ? (
        <div className="flex flex-1 flex-col items-center justify-center text-center gap-1.5">
          <p className="text-sm text-gray-400">
            {t('tasks.communication.noThread')}
          </p>
          <p className="text-xs text-gray-400">
            {t('tasks.communication.noThreadSub')}
          </p>
        </div>
      ) : (
        <>
          <div
            ref={messagesContainerRef}
            className={
              listClassName ??
              'flex-1 max-h-[350px] space-y-3 pr-1 mb-3 overflow-y-auto'
            }
          >
            {messages.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-400">
                {t('tasks.communication.noMessages')}
              </p>
            ) : (
              messages.map((msg) => (
                <TaskMessageBubble
                  key={msg.id}
                  message={msg}
                  isOwn={msg.senderUserId === currentUserId}
                  accent={accent}
                  language={language}
                  {...messageBubbleProps}
                  attachmentsSlot={
                    (msg.attachments?.length ?? 0) > 0 && (
                      <div className="mt-2 space-y-1">
                        {msg.attachments.map((att) => (
                          <AttachmentRow
                            key={att.id}
                            fileName={att.fileName}
                            fileUrl={att.fileUrl}
                            labels={{
                              fallback: t(
                                'tasks.communication.attachmentFallback'
                              ),
                              view: t('tasks.communication.attachmentView'),
                            }}
                          />
                        ))}
                      </div>
                    )
                  }
                />
              ))
            )}
          </div>

          <div className="border-t border-gray-100 pt-3 flex gap-2 flex-shrink-0">
            <div className="flex flex-1 flex-col gap-1.5">
              <Textarea
                rows={2}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('tasks.communication.placeholder')}
                className={`w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-1 ${TEXTAREA_FOCUS_ACCENT[accent]}`}
              />
              <TaskAttachmentUploadArea
                staged={attachments.staged}
                onAdd={attachments.addFiles}
                onRemove={attachments.removeStaged}
                failedLabel={t('tasks.communication.uploadFailed')}
                attachLabel={t('tasks.communication.attach')}
                showTriggerButton
                accent={accent}
              />
            </div>
            <Button
              onClick={handleSend}
              size="sm"
              disabled={
                !messageText.trim() ||
                !threadId ||
                isSending ||
                attachments.hasUploading
              }
              className={`self-end rounded-lg p-2 text-white disabled:opacity-50 transition-colors ${SEND_BUTTON_ACCENT[accent]}`}
            >
              <SendIcon size={16} />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
